import type { CommentNode, CommentShapeLocation, CommentDiagramLocation, CommentAxisAnchor } from '../domains/comments/models/Comment';
import type { Node } from '../core/models/scene';

/**
 * Resolves a threaded comment tree into absolute diagram coordinates, filling
 * `locOnDiagram` for comment nodes that only store shape-relative anchors.
 */
export function positionCommentNode(comment: CommentNode, nodesById: Map<string, Node>): CommentNode {
  const positionedReplies = comment.replies.map((reply) => positionCommentNode(reply, nodesById));

  const node = comment.shapeId ? nodesById.get(comment.shapeId) : undefined;
  const derivedLocation = node && comment.locOnShape
    ? computeDiagramPointFromShape(node, comment.locOnShape)
    : null;

  return {
    ...comment,
    locOnDiagram: derivedLocation ?? comment.locOnDiagram,
    replies: positionedReplies,
  };
}

/**
 * Converts a shape-relative anchor into a diagram coordinate using the node's bounds.
 */
export function computeDiagramPointFromShape(node: Node, locOnShape: CommentShapeLocation): CommentDiagramLocation | null {
  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  const width = pickPositiveNumber(
    node.width,
    node.w,
    attrs.width as number | undefined,
    attrs.w as number | undefined,
  );
  const height = pickPositiveNumber(
    node.height,
    node.h,
    attrs.height as number | undefined,
    attrs.h as number | undefined,
  );

  if (width <= 0 || height <= 0) {
    return null;
  }

  const offsetX = resolveAxisOffset(locOnShape.x, width);
  const offsetY = resolveAxisOffset(locOnShape.y, height);

  // Rotation offsets are not handled yet; extend this when rotated nodes support anchored comments.
  return {
    x: node.x + offsetX,
    y: node.y + offsetY,
  };
}

function resolveAxisOffset(anchor: CommentAxisAnchor | undefined, length: number): number {
  if (!anchor) {
    return length / 2;
  }

  const clamp = (value: number) => {
    if (!Number.isFinite(value)) {
      return 0;
    }
    return Math.min(Math.max(value, 0), length);
  };

  switch (anchor.type) {
    case 'fixed-start':
      return clamp(anchor.value);
    case 'fixed-end':
      return clamp(length - anchor.value);
    case 'relative':
    default:
      return clamp(anchor.value * length);
  }
}

function pickPositiveNumber(...candidates: Array<number | undefined>): number {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
      return candidate;
    }
  }
  return 0;
}
